import type {
  ImplantationDto,
  ImplantationStepDto,
  ImportBusinessResultDto,
} from "@iagenix/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  ExternalLink,
  Hand,
  Loader2,
  MinusCircle,
  Play,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { knownIds, manualLink, runAutomaticSteps, saveStep } from "@/components/implantation/run";
import { PageHeader } from "@/components/page-header";
import { ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { STATUS_LABEL } from "./implantacao";

export const Route = createFileRoute("/app/$workspaceId/implantacao_/$implantationId")({
  validateSearch: z.object({ executar: z.boolean().optional() }),
  component: ImplantacaoDetalhePage,
});

const STEP_STATUS: Record<
  ImplantationStepDto["status"],
  { label: string; icon: typeof CheckCircle2; className: string }
> = {
  pendente: { label: "Na fila", icon: CircleDashed, className: "text-muted-foreground" },
  executando: { label: "Executando", icon: Loader2, className: "animate-spin text-primary" },
  concluido: { label: "Concluído", icon: CheckCircle2, className: "text-emerald-600" },
  manual: { label: "Você precisa fazer", icon: Hand, className: "text-amber-600" },
  aguardando: { label: "Aguardando a Meta", icon: CircleDashed, className: "text-amber-600" },
  erro: { label: "Erro", icon: CircleAlert, className: "text-destructive" },
  pulado: { label: "Pulado", icon: MinusCircle, className: "text-muted-foreground" },
};

function ImplantacaoDetalhePage() {
  const { workspaceId, implantationId } = Route.useParams();
  const { executar } = Route.useSearch();
  const queryClient = useQueryClient();
  const key = ["workspaces", workspaceId, "implantations", implantationId];
  const impl = useQuery({
    queryKey: key,
    queryFn: () =>
      api<ImplantationDto>(`/api/workspaces/${workspaceId}/implantations/${implantationId}`),
  });
  const pages = useQuery(queries.pages(workspaceId));
  const bms = useQuery(queries.businesses(workspaceId));
  const [running, setRunning] = useState(false);
  const [busyStep, setBusyStep] = useState<string | null>(null);
  const autoStarted = useRef(false);
  const navigate = useNavigate();
  const [novoNomeBm, setNovoNomeBm] = useState("");

  const set = (next: ImplantationDto) => queryClient.setQueryData(key, next);
  const refreshMeta = async () => {
    const connectionId = impl.data?.input.connectionId;
    if (connectionId)
      await api(`/api/workspaces/${workspaceId}/meta/connections/${connectionId}/sync`, {
        method: "POST",
      }).catch(() => undefined);
    await new Promise((r) => setTimeout(r, 4000));
    await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });
  };

  const executarAutomaticas = async (from: ImplantationDto) => {
    setRunning(true);
    try {
      const done = await runAutomaticSteps(workspaceId, from, set);
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });
      if (done.status === "concluida") toast.success("Implantação concluída.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (executar && impl.data && !autoStarted.current) {
      autoStarted.current = true;
      // Tira o ?executar da URL para não disparar de novo ao recarregar a tela.
      void navigate({
        to: "/app/$workspaceId/implantacao/$implantationId",
        params: { workspaceId, implantationId },
        search: {},
        replace: true,
      });
      void executarAutomaticas(impl.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara uma vez ao chegar do formulário
  }, [executar, impl.data]);

  if (impl.isPending) return <LoadingState rows={6} />;
  if (impl.isError) return <ErrorState error={impl.error} onRetry={() => void impl.refetch()} />;
  const data = impl.data;
  const ids = knownIds(data);
  const pendentesAuto = data.steps.some(
    (s) => s.kind === "auto" && (s.status === "pendente" || s.status === "erro"),
  );

  const marcar = async (
    step: ImplantationStepDto,
    status: ImplantationStepDto["status"],
    message: string | null = null,
  ) => {
    setBusyStep(step.key);
    try {
      const next = await saveStep(workspaceId, data, step.key, { status, message });
      set(next);
      return next;
    } catch (err) {
      toast.error(errorMessage(err));
      return data;
    } finally {
      setBusyStep(null);
    }
  };

  /** "Já fiz": confere na Meta quando dá; senão registra a confirmação. */
  const jaFiz = async (step: ImplantationStepDto) => {
    setBusyStep(step.key);
    try {
      if (step.key === "instagram") {
        await refreshMeta();
        const list = await queryClient.query(queries.pages(workspaceId));
        const page = list.find((p) => p.metaId === ids.pageMetaId);
        if (!page?.instagramAccountId) {
          toast.error(
            "Ainda não encontrei Instagram ligado a essa página. Confira e tente de novo.",
          );
          return;
        }
        set(
          await saveStep(workspaceId, data, "instagram", {
            status: "concluido",
            message: `@${page.instagramUsername ?? "instagram"} ligado.`,
          }),
        );
      } else if (step.key === "bm") {
        const r = await api<ImportBusinessResultDto>(
          `/api/workspaces/${workspaceId}/meta/businesses/import`,
          { method: "POST" },
        );
        const wanted = data.input.business.mode === "novo" ? data.input.business.name : "";
        const found =
          r.imported.find((b) => b.name.trim().toLowerCase() === wanted.trim().toLowerCase()) ??
          r.imported[0];
        if (!found) {
          toast.error("Nenhum BM novo apareceu ainda. Confira se ele foi criado e tente de novo.");
          return;
        }
        const next = await saveStep(workspaceId, data, "bm", {
          status: "concluido",
          message: `BM "${found.name}" importado.`,
          result: { businessId: found.id, businessMetaId: found.metaId },
        });
        set(next);
        await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });
        void executarAutomaticas(next);
      } else if (step.key === "verificacao") {
        const list = await queryClient.query(queries.businesses(workspaceId));
        const bm = list.find((b) => b.id === ids.businessId);
        if (bm?.verificationStatus === "verified") {
          set(
            await saveStep(workspaceId, data, "verificacao", {
              status: "concluido",
              message: "Empresa verificada.",
            }),
          );
        } else {
          set(
            await saveStep(workspaceId, data, "verificacao", {
              status: "aguardando",
              message: "Enviada. A Meta costuma levar de dias a semanas.",
            }),
          );
        }
      } else {
        set(
          await saveStep(workspaceId, data, step.key, {
            status: "concluido",
            message: "Confirmado por você.",
          }),
        );
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyStep(null);
    }
  };

  const tentarOutroNomeBm = async () => {
    const nome = novoNomeBm.trim();
    if (nome.length < 2) {
      toast.error("Informe o novo nome do BM.");
      return;
    }
    setBusyStep("bm");
    try {
      await api(`/api/workspaces/${workspaceId}/implantations/${data.id}`, {
        method: "PATCH",
        body: { businessName: nome },
      });
      const next = await saveStep(workspaceId, data, "bm", {
        status: "pendente",
        message: `Tentando com o nome "${nome}".`,
      });
      set(next);
      setNovoNomeBm("");
      void executarAutomaticas(next);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyStep(null);
    }
  };

  /** BM que já existe na Meta (criado à mão ou automaticamente junto com a página). */
  const escolherBm = async (businessId: string) => {
    const bm = (bms.data ?? []).find((b) => b.id === businessId);
    if (!bm) return;
    setBusyStep("bm");
    try {
      const next = await saveStep(workspaceId, data, "bm", {
        status: "concluido",
        message: `BM "${bm.name}" (${bm.metaId}) escolhido.`,
        result: { businessId: bm.id, businessMetaId: bm.metaId },
      });
      set(next);
      void executarAutomaticas(next);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyStep(null);
    }
  };

  const escolherPagina = async (pageMetaId: string) => {
    setBusyStep("pagina");
    try {
      await api(`/api/workspaces/${workspaceId}/implantations/${data.id}`, {
        method: "PATCH",
        body: { pageMetaId },
      });
      const next = await saveStep(workspaceId, { ...data }, "pagina", {
        status: "concluido",
        message: "Página escolhida.",
      });
      set(next);
      void executarAutomaticas(next);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyStep(null);
    }
  };

  return (
    <>
      <PageHeader
        title={data.name}
        description="As etapas automáticas rodam em ordem. As marcadas com a mão são suas: abra o link, faça e clique em Já fiz."
        actions={
          <>
            <Badge variant={data.status === "concluida" ? "default" : "secondary"}>
              {STATUS_LABEL[data.status]}
            </Badge>
            {pendentesAuto ? (
              <Button onClick={() => void executarAutomaticas(data)} loading={running}>
                <Play />
                Executar etapas automáticas
              </Button>
            ) : null}
          </>
        }
      />
      <Card>
        <CardContent>
          <ol className="divide-y">
            {data.steps.map((step, i) => {
              const st = STEP_STATUS[step.status];
              const Icon = st.icon;
              const link =
                step.status === "manual" || step.status === "aguardando" || step.status === "erro"
                  ? manualLink(step.key, data, bms.data ?? [])
                  : null;
              const aberta = step.status !== "concluido" && step.status !== "pulado";
              return (
                <li key={step.key} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
                  <Icon className={`mt-0.5 size-5 shrink-0 ${st.className}`} aria-hidden />
                  <div className="flex-1 space-y-1">
                    <p className="font-medium">
                      {i + 1}. {step.label}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        · {step.kind === "auto" ? "automática" : "manual"} · {st.label}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">{step.message ?? step.help}</p>
                    {step.key === "pagina" && aberta ? (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Select onValueChange={(v) => void escolherPagina(v)}>
                          <SelectTrigger className="w-72">
                            <SelectValue placeholder="Já criei: escolher a página" />
                          </SelectTrigger>
                          <SelectContent>
                            {(pages.data ?? []).map((p) => (
                              <SelectItem key={p.metaId} value={p.metaId}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => void refreshMeta()}>
                          Atualizar lista
                        </Button>
                      </div>
                    ) : null}
                    {step.key === "bm" && (step.status === "manual" || step.status === "erro") ? (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Select onValueChange={(v) => void escolherBm(v)}>
                          <SelectTrigger className="w-72">
                            <SelectValue placeholder="Já existe: escolher o BM" />
                          </SelectTrigger>
                          <SelectContent>
                            {(bms.data ?? [])
                              .filter((b) => b.permittedRoles.includes("ADMIN"))
                              .map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                  {b.name} ({b.metaId})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => void refreshMeta()}>
                          Atualizar lista
                        </Button>
                      </div>
                    ) : null}
                    {step.key === "bm" &&
                    data.input.business.mode === "novo" &&
                    (step.status === "manual" || step.status === "erro") ? (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Input
                          className="w-72"
                          value={novoNomeBm}
                          onChange={(e) => setNovoNomeBm(e.target.value)}
                          placeholder="Outro nome para o BM (ex.: IA GENIX Cloud)"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void tentarOutroNomeBm()}
                          loading={busyStep === "bm"}
                        >
                          Tentar com este nome
                        </Button>
                      </div>
                    ) : null}
                    {step.key === "campanha" && ids.draftId ? (
                      <Button asChild size="sm" variant="outline" className="mt-1">
                        <Link
                          to="/app/$workspaceId/assistente/$draftId"
                          params={{ workspaceId, draftId: ids.draftId }}
                        >
                          Abrir rascunho e publicar
                        </Link>
                      </Button>
                    ) : null}
                  </div>
                  {aberta && step.key !== "pagina" ? (
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      {link ? (
                        <Button asChild size="sm" variant="outline">
                          <a href={link} target="_blank" rel="noreferrer">
                            Abrir na Meta
                            <ExternalLink />
                          </a>
                        </Button>
                      ) : null}
                      {step.kind === "manual" || step.status === "manual" ? (
                        <Button
                          size="sm"
                          onClick={() => void jaFiz(step)}
                          loading={busyStep === step.key}
                        >
                          {step.key === "bm" ? "Importar BMs novos" : "Já fiz"}
                        </Button>
                      ) : null}
                      {step.status === "erro" || step.status === "manual" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void marcar(step, "pulado", "Pulada por você.")}
                        >
                          Pular
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>
    </>
  );
}
